/**
 * Google AI grounding often returns vertexaisearch redirect URIs.
 * LinkedIn /in slugs and publication dates live on the resolved URL or in the answer text.
 */

import { isConsultantSiteEmail } from '../workshop-pack/intake-conversation.js'

const GROUNDING_REDIRECT_HOST = 'vertexaisearch.cloud.google.com'
const ISO_DATE = /\b(20\d{2}-\d{2}-\d{2})\b/
const MONTH_DATE =
  /\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,2},\s+20\d{2}\b/i
const HTTP_URL = /https?:\/\/[^\s<>"')\]]+/gi

function ownSiteHost(value: string | null | undefined): string {
  return (value || '').trim().replace(/^www\./i, '').toLowerCase()
}

/**
 * Our own site is not an external source. Citing it does not ground a claim.
 * Apex only: www.farzadbayat.com counts, blog.farzadbayat.com does not.
 */
export function isOwnSiteUri(uri: string | null | undefined): boolean {
  if (!uri) return false
  try {
    const host = ownSiteHost(new URL(uri).hostname)
    if (host.split('.').filter(Boolean).length > 2) return false
    return isConsultantSiteEmail(`lead@${host}`)
  } catch {
    return uri.toLowerCase().includes('farzadbayat.com') && isConsultantSiteEmail('lead@farzadbayat.com')
  }
}

/** True for our own domain, including a subdomain. A test or staff email on it says nothing about the client. */
export function isOwnSiteDomain(domain: string | null | undefined): boolean {
  const host = ownSiteHost(domain)
  if (!host) return false
  return isConsultantSiteEmail(`lead@${host}`)
}

/** Directories and search engines. They are not a lead's company site. */
const SOCIAL_OR_SEARCH_HOSTS = [
  'linkedin.com',
  'google.com',
  'bing.com',
  'duckduckgo.com',
  'facebook.com',
  'instagram.com',
  'x.com',
  'twitter.com',
  'youtube.com',
  'tiktok.com',
] as const

export function isSocialOrSearchHost(domain: string | null | undefined): boolean {
  const host = ownSiteHost(domain)
  if (!host) return false
  return SOCIAL_OR_SEARCH_HOSTS.some(item => host === item || host.endsWith(`.${item}`))
}

function letters(value: string, oSlash: 'o' | 'oe'): string {
  return value
    .toLowerCase()
    .replace(/æ/g, 'ae').replace(/ø/g, oSlash).replace(/å/g, 'a')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\b(as|asa|ab|ans|da|sa|ltd|llc|inc|gmbh|oy|aps)\b/g, ' ')
    .replace(/[^a-z0-9]/g, '')
}

function nameMatchesLabel(
  name: string | null | undefined,
  domain: string | null | undefined,
  oSlash: 'o' | 'oe',
): boolean {
  const label = letters((domain || '').replace(/^www\./i, '').split('.')[0] || '', oSlash)
  const firm = letters(name || '', oSlash)
  if (!label || !firm) return false
  // dnb.no is 3 letters and matches. Shorter stems (vy.no) match the whole label, not a slice.
  if (label.length < 3 || firm.length < 3) {
    return label.length >= 2 && firm.length >= 2 && (firm === label || firm.startsWith(label))
  }
  return firm.includes(label) || label.includes(firm)
}

/**
 * True when the email domain plausibly belongs to the typed firm name
 * («Nitor AS» ↔ nitor.no, «Proff Rørlegger AS» ↔ proffrorlegger.no).
 * ø/æ written as oe/ae still match («Bølgen Bil AS» ↔ boelgenbil.no).
 * The ø→o fold stays, so «Proff Rørlegger AS» ↔ proffrorlegger.no still matches.
 */
export function companyNameMatchesDomain(name: string | null | undefined, domain: string | null | undefined): boolean {
  return nameMatchesLabel(name, domain, 'o') || nameMatchesLabel(name, domain, 'oe')
}

export function isGroundingRedirectUri(uri: string | null | undefined): boolean {
  if (!uri) return false
  try {
    const url = new URL(uri)
    return (
      url.protocol === 'https:' &&
      url.hostname === GROUNDING_REDIRECT_HOST &&
      url.pathname.includes('grounding-api-redirect')
    )
  } catch {
    return false
  }
}

export function publishedAtFromText(text: string | null | undefined): string | null {
  if (!text) return null
  const iso = text.match(ISO_DATE)
  if (iso?.[1]) return iso[1]
  const month = text.match(MONTH_DATE)
  if (!month?.[0]) return null
  const parsed = new Date(month[0])
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toISOString().slice(0, 10)
}

export function extractHttpUrls(text: string | null | undefined): string[] {
  if (!text) return []
  const seen = new Set<string>()
  const out: string[] = []
  for (const match of text.matchAll(HTTP_URL)) {
    const raw = match[0]?.replace(/[),.;]+$/, '')
    if (!raw || seen.has(raw)) continue
    try {
      const url = new URL(raw)
      if (url.protocol !== 'http:' && url.protocol !== 'https:') continue
      seen.add(url.toString())
      out.push(url.toString())
    } catch {
      // skip
    }
  }
  return out
}

const OWN_SITE_IDENTITY_TEXT = /farzadbayat\.com|farzad\s+bayat/i

/**
 * Drop sentences that name our site or «Farzad Bayat» from lead research prose:
 * the company summary, a source excerpt, and the lead-context fields copied from that research.
 * Never run this on the agent identity, a system prompt, or about-Farzad content.
 * Those still name Farzad Bayat and farzadbayat.com on purpose.
 */
export function omitOwnSiteIdentityText(value: string | null | undefined): string {
  if (!value?.trim()) return ''
  return value
    .split(/(?<=[.!?])\s+|\n+/)
    .map(part => part.trim())
    .filter(part => part && !OWN_SITE_IDENTITY_TEXT.test(part))
    .join(' ')
    .trim()
}

/** Follow a grounding redirect to the public URL. Fail open to the original URI. */
export async function resolveGroundingRedirect(uri: string): Promise<string> {
  if (!isGroundingRedirectUri(uri)) return uri
  try {
    const response = await fetch(uri, {
      method: 'GET',
      redirect: 'follow',
      signal: AbortSignal.timeout(2500),
      headers: { Accept: 'text/html,application/xhtml+xml' },
    })
    await response.body?.cancel().catch(() => undefined)
    const finalUrl = response.url
    if (finalUrl && /^https:\/\//i.test(finalUrl) && !isGroundingRedirectUri(finalUrl)) {
      return finalUrl
    }
  } catch {
    // keep the redirect URI; callers also scan the answer text
  }
  return uri
}
